import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, Mail, MessageCircle, Sparkles, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackContactAction } from "@/utils/contactTracking";
import { useServiceList } from "@/contexts/ServiceListContext";

interface BookingDetails {
  serviceType?: string;
  quantities?: Record<string, Record<string, number>>;
  pelviPowerQuantity?: number;
  saunaQuantity?: number;
}

interface SpaContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentModal?: string;
  bookingDetails?: BookingDetails;
}

const SpaContactModal: React.FC<SpaContactModalProps> = ({
  isOpen,
  onClose,
  parentModal,
  bookingDetails,
}) => {
  const { t } = useLanguage();
  const { setIsTrayOpen } = useServiceList();

  // Helper function to format booking summary
  const getBookingSummary = (): string[] => {
    if (!bookingDetails) return [];
    
    const summary: string[] = [];
    
    if (bookingDetails.serviceType === "pelvipower" && bookingDetails.pelviPowerQuantity && bookingDetails.pelviPowerQuantity > 0) {
      summary.push(`PelviPower™ Training: ${bookingDetails.pelviPowerQuantity} session${bookingDetails.pelviPowerQuantity > 1 ? 's' : ''} (${bookingDetails.pelviPowerQuantity * 29} CHF)`);
    }
    
    if (bookingDetails.serviceType === "sauna" && bookingDetails.saunaQuantity && bookingDetails.saunaQuantity > 0) {
      summary.push(`Sauna & Spa Package: ${bookingDetails.saunaQuantity} session${bookingDetails.saunaQuantity > 1 ? 's' : ''} (${bookingDetails.saunaQuantity * 45} CHF)`);
    }
    
    if (bookingDetails.serviceType === "massages" && bookingDetails.quantities) {
      // This would need more complex logic to match service IDs to names
      // For now, we'll just indicate that massages are selected
      const totalMassages = Object.values(bookingDetails.quantities).reduce((acc, durations) => {
        return acc + Object.values(durations).reduce((sum, qty) => sum + qty, 0);
      }, 0);
      if (totalMassages > 0) {
        summary.push(`Therapeutic Massages: ${totalMassages} treatment${totalMassages > 1 ? 's' : ''} selected`);
      }
    }
    
    return summary;
  };

  const bookingSummary = getBookingSummary();

  // Track modal open with parent context
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all tracking variables first (prevents data pollution)
      // NOTE: button_text and button_location are preserved to retain context of how modal was opened
      window.dataLayer.push({
        // Clear modal variables (will be reset below)
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Preserve button variables (retain context of triggering action)
        // button_text: undefined,     // ← Keep this!
        // button_location: undefined, // ← Keep this!
        // Clear booking variables
        room_type: undefined,
        // Clear contact action variables
        contact_type: undefined,
        contact_source: undefined,
        event_category: undefined,
        event_label: undefined,
        phone_number: undefined,
        email_address: undefined,
        whatsapp_number: undefined,
        region: undefined,
        destination: undefined,
        booking_method: undefined,
        // Clear event consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
        // Clear Calendly variables
        calendly_action: undefined,
        calendly_location: undefined,
      });

      // Now push the actual event with clean data
      window.dataLayer.push({
        event: "contact_spa_modal_open",
        modal_type: "spa_contact",
        modal_name: "spa_contact",
        ...(parentModal && { parent_modal: parentModal }),
        page_location: window.location.pathname,
      });
    }
  }, [isOpen, parentModal]);

  const contactOptions = [
    {
      icon: Phone,
      title: t("contactModals.spa.phoneTitle"),
      description: t("contactModals.spa.phoneDesc"),
      contact: "+41 33 841 00 25",
      action: "tel:+41338410025",
      primary: true,
    },
    {
      icon: Mail,
      title: t("contactModals.spa.emailTitle"),
      description: t("contactModals.spa.emailDesc"),
      contact: "info@beatenberg-resortspa.ch",
      action: "mailto:info@beatenberg-resortspa.ch",
      primary: false,
    },
    {
      icon: MapPin,
      title: t("contactModals.spa.locationTitle"),
      description: t("contactModals.spa.locationDesc"),
      contact: "Beatenberg Resort & Spa",
      action:
        "https://www.google.com/maps/place/Parkhotel+Beatenberg/@46.6976742,7.7919849,17z/data=!3m1!4b1!4m9!3m8!1s0x478fa541a569a32d:0x786359f5bdf1a5e4!5m2!4m1!1i2!8m2!3d46.6976705!4d7.7945598!16s%2Fg%2F1tkc5ln2?entry=ttu&g_ep=EgoyMDI1MDkwOS4wIKXMDSoASAFQAw%3D%3D",
      primary: false,
    },
    {
      icon: MessageCircle,
      title: t("contactModals.spa.whatsappTitle"),
      description: t("contactModals.spa.whatsappDesc"),
      contact: "Europe: +41 76 622 01 22 | Asia: +41 79 696 86 08",
      action: "https://wa.me/41766220122",
      primary: false,
    },
  ];

  return (
    <AnimatePresence data-oid="n2r55k-">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
            style={{ zIndex: 9999 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-oid="zpaiet:"
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-oid="591o31r"
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              data-oid="atv8elm"
            >
              {/* Header */}
              <div
                className="relative bg-gradient-to-r from-[#A38648] to-[#A38648]/80 text-white p-6 rounded-t-2xl"
                data-oid="irtk5dt"
              >
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  data-oid="s:_hksm"
                >
                  <X size={20} data-oid="al0:1dw" />
                </button>

                <div className="flex items-center gap-3" data-oid="jzx-z44">
                  <div
                    className="p-3 bg-white/20 rounded-full"
                    data-oid="nkxo-99"
                  >
                    <Sparkles size={24} data-oid="669.pk4" />
                  </div>
                  <div data-oid="t-9d4y_">
                    <h2
                      className="text-2xl font-playfair font-bold"
                      data-oid="oh.5o52"
                    >
                      {t("contactModals.spa.title")}
                    </h2>
                    <p className="text-white/90 font-lato" data-oid="z.l_i43">
                      {t("contactModals.spa.subtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-oid="_l7naub">
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
                  data-oid="y1s8jko"
                >
                  {contactOptions.map((option, index) => {
                    const IconComponent = option.icon;
                    return (
                      <motion.div
                        key={option.title}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg h-full ${
                          option.primary
                            ? "border-[#A38648] bg-[#A38648]/5"
                            : "border-gray-200 hover:border-[#A38648]/50"
                        }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        data-oid=".ev18sx"
                      >
                        <div
                          className="flex items-start gap-3"
                          data-oid=".9-mc8s"
                        >
                          <div
                            className={`p-2 rounded-lg flex-shrink-0 ${
                              option.primary
                                ? "bg-[#A38648] text-white"
                                : "bg-gray-100 text-[#A38648]"
                            }`}
                            data-oid="eprio78"
                          >
                            <IconComponent size={20} data-oid="c.jbf.t" />
                          </div>
                          <div className="flex-1" data-oid="r8qte:4">
                            <h3
                              className="font-semibold text-gray-900 mb-1"
                              data-oid="2jxpbwu"
                            >
                              {option.title}
                            </h3>
                            <p
                              className="text-sm text-gray-600 mb-2"
                              data-oid="f4i5oqq"
                            >
                              {option.description}
                            </p>
                            {option.title === "WhatsApp Spa" ? (
                              <div className="space-y-2" data-oid="4sj7y-_">
                                <a
                                  href="https://wa.me/41766220122"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() =>
                                    trackContactAction("spa", "whatsapp", {
                                      region: "europe",
                                      whatsapp_number: "+41766220122",
                                      ...(parentModal && {
                                        parent_modal: parentModal,
                                      }),
                                    })
                                  }
                                  className="contact-action-track block px-3 py-2 rounded-full text-xs font-medium bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                                  data-oid="ww8:8.u"
                                >
                                  Europe: +41 76 622 01 22
                                </a>
                                <a
                                  href="https://wa.me/41796968608"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() =>
                                    trackContactAction("spa", "whatsapp", {
                                      region: "asia",
                                      whatsapp_number: "+41796968608",
                                      ...(parentModal && {
                                        parent_modal: parentModal,
                                      }),
                                    })
                                  }
                                  className="contact-action-track block px-3 py-2 rounded-full text-xs font-medium bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                                  data-oid="krbzojj"
                                >
                                  Asia: +41 79 696 86 08
                                </a>
                              </div>
                            ) : option.action ? (
                              <a
                                href={option.action}
                                target={
                                  option.action.startsWith("http")
                                    ? "_blank"
                                    : undefined
                                }
                                rel={
                                  option.action.startsWith("http")
                                    ? "noopener noreferrer"
                                    : undefined
                                }
                                onClick={() => {
                                  let actionType: "phone" | "email" | "location" =
                                    "phone";

                                  if (option.action.startsWith("tel:")) {
                                    actionType = "phone";
                                  } else if (
                                    option.action.startsWith("mailto:")
                                  ) {
                                    actionType = "email";
                                  } else if (
                                    option.icon === MapPin ||
                                    option.action.includes("google.com/maps")
                                  ) {
                                    actionType = "location";
                                  }

                                  const details: Record<string, any> = {};
                                  if (actionType === "phone") {
                                    details.phone_number = "+41338410025";
                                  }
                                  if (actionType === "email") {
                                    details.email_address =
                                      "info@beatenberg-resortspa.ch";
                                  }
                                  if (actionType === "location") {
                                    details.location = "parkhotel_beatenberg";
                                    details.destination = "parkhotel_beatenberg";
                                  }
                                  if (parentModal)
                                    details.parent_modal = parentModal;

                                  trackContactAction("spa", actionType, details);
                                }}
                                className={`contact-action-track inline-block px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                                  option.primary
                                    ? "bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                data-oid="-pq8n25"
                              >
                                {option.contact}
                              </a>
                            ) : (
                              <span
                                className="inline-block px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap"
                                data-oid="3sxyz1o"
                              >
                                {option.contact}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Booking Summary */}
                {bookingSummary.length > 0 && (
                  <div
                    className="bg-gradient-to-r from-alpine-gold/10 to-alpine-gold/5 border border-alpine-gold/20 rounded-xl p-4 mb-6"
                    data-oid="booking-summary"
                  >
                    <h4
                      className="font-semibold text-gray-900 mb-2 flex items-center gap-2"
                      data-oid="booking-summary-title"
                    >
                      <Sparkles className="w-4 h-4 text-alpine-gold" />
                      Your Booking Request
                    </h4>
                    <ul
                      className="text-sm text-gray-700 space-y-1"
                      data-oid="booking-summary-list"
                    >
                      {bookingSummary.map((item, index) => (
                        <li key={index} className="flex items-center gap-2" data-oid={`booking-item-${index}`}>
                          <span className="text-alpine-gold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Spa Services Info */}
                <div
                  className="bg-gradient-to-r from-pebble-beige to-pebble-beige/50 rounded-xl p-4 mb-6"
                  data-oid="7d4glok"
                >
                  <h4
                    className="font-semibold text-gray-900 mb-2"
                    data-oid="urfnzqm"
                  >
                    {t("contactModals.spa.servicesTitle")}
                  </h4>
                  <ul
                    className="text-sm text-gray-700 space-y-1"
                    data-oid="91cfvr:"
                  >
                    {t("contactModals.spa.servicesList").map(
                      (service: string, index: number) => (
                        <li key={index} data-oid="nck5n0x">
                          • {service}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                {/* Quick Actions */}
                <div
                  className="flex justify-center gap-3"
                  data-oid="l76ieeg"
                >
                  <Button
                    trackingType="none"
                    className="px-8 bg-alpine-gold hover:bg-alpine-gold/90 text-white"
                    onClick={() => {
                      // Open spa tray with dedicated source for analytics
                      setIsTrayOpen(true, "spa_contact_modal");
                      onClose();
                    }}
                  >
                    {t("contactModals.spa.bookButton")}
                  </Button>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="px-8"
                    data-oid="s232rkf"
                  >
                    {t("contactModals.spa.closeButton")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SpaContactModal;
